import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewEncapsulation,
	ChangeDetectorRef,
	ChangeDetectionStrategy,
	ViewChildren,
	QueryList,
	SimpleChanges,
	OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

import {
	AccountCarrierMapDTO,
	CarrierDTOResponse,
	ContactsTable,
	DATE_MDY,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	ProjectCarrierMapDTO,
	ProjectDTO,
	RestService,
	TruckSearchTable,
	Owner,
	IS_EMPTY,
	GET_CARRIER_BUSINESS_NAME,
	CONTACT_LIST_TABLE,
	IS_EMPTY_LIST,
	BUILD_LANE,
	FORMAT_LOCATION,
} from 'cargo-chief-commons';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { ProjectHelperService } from '@service/projectHelper/project-helper.service';
import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { CarrierFeedbackService } from '@service/carrierFeedback/carrier-feedback.service';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { ACCOUNT_TYPE, COPY_TO_CLIPBOARD_MSGS } from '@domain/constants/app-variables';
import { ProductsService } from '@service/products/products.service';
import { UserCarrierMapHelperService } from '@service/userCarrierMapHelper/user-carrier-map-helper.service';
import { SharedService } from '@service/shared/shared.service';
import { CarrierVettingService } from '@service/carrier-vetting/carrier-vetting.service';
import { SHOW_IN_NETWORK_LOGO, SHOW_ONBOARD_CARRIER_LINK } from '@shared/utils/in-network';
import { IntegrationService } from '@service/integration/integration.service';
import { Store } from '@ngrx/store';
import { PROJECT_CARRIER_MAP } from '../store/actions/';
import { projectDetailsPageStateKey } from '../store/state';
import { takeUntil } from 'rxjs/operators';
import { Observable, of, take } from 'rxjs';
import { BaseComponent } from '@shared/base/base.component';
import { HaulpayService } from '@service/haulpay/haulpay.service';
import { environment } from '@environment/environment';

@Component({
	selector: 'app-procured-carriers',
	templateUrl: './procured-carriers.component.html',
	styleUrls: ['./procured-carriers.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProcuredCarriersComponent extends BaseComponent implements OnInit, OnDestroy {
	@Input() projectCarrierMapList: Array<ProjectCarrierMapDTO> = [];
	@Input() selectedProjectCarrierMapIDs: Array<string> = [];
	@Input() isCarrierRatesEnabled: boolean = false;
	@Input() accountCarrierLanes: any = {};
	@Input() projectDTO: ProjectDTO = {} as ProjectDTO;
	@Input() allProjectsList: Array<ProjectDTO> = [];
	@Input() projectsNextPage: boolean = false;
	@Input() isLoadingProjectsList: boolean = false;
	@Input() totalNumberOfProjects = 0;
	@Input() isLoadingAccountCarrierLanes: boolean = false;
	@Input() searchTruckForm: any = null;
	@Input() usersList: Array<Owner> = [];
	@Input() allUsersList: Array<Owner> = [];
	@Input() userCarrierMapList: Array<any> = [];
	@Input() userCarrierMap: any = {};
	@Input() isCarrierVettingIntegrationEnabled!: boolean;
	@Input() CARRIER_QUALITY_ENABLED!: boolean;
	@Input() showProcuredCarriersForGroup!: boolean;
	@Input() isWorkflowProject: boolean = false;
	@Input() carrierAllocationsMap: { [carrierId: string]: any } = {};
	@Input() hideLastColumn: boolean = false; // CN-8312: Hide Lanes/Notes/Projects column for V2/V3 layouts
	@Input() isKanbanView: boolean = false; // CN-8312: Compact card layout for V3 Kanban view
	@Input() showBulkSelectGutter: boolean = false; // CN-8312 V2: render leading checkbox column for bulk select
	@Input() enableOnboardCarrier: boolean = false; // CN-8774: render the Onboard Carrier link (Engaged tab only)

	// CN-8312 V2: when the Assign Rep column renders in V2 row 1, the row needs
	// reduced widths so the total stays at 100% (otherwise it sums to 119% and
	// flexbox falls back to equal distribution, breaking column alignment).
	// Mirrors the *ngIf condition on the Assign Rep <td> so the widths flip
	// in lock-step with the cell's visibility.
	get isAssignRepColumnVisibleInV2(): boolean {
		return (
			this.hideLastColumn &&
			this.productsService.ASSIGN_CARRIERS &&
			IS_NOT_NULL_OR_UNDEFINED(this.usersList) &&
			this.usersList.length > 0 &&
			IS_NOT_NULL_OR_UNDEFINED(this.userCarrierMapList)
		);
	}

	@Output() openFullProfileEvent: EventEmitter<{ projectCarrierMap: any; event: MouseEvent }> =
		new EventEmitter();
	@Output() bulkSelectToggle: EventEmitter<ProjectCarrierMapDTO> = new EventEmitter();
	@Output() fireSegmentEvent: EventEmitter<{
		projectId: string;
		carrierId: string;
		cIndex: number;
	}> = new EventEmitter();
	@Output() panelClicked: EventEmitter<{
		projectCarrierMap: ProjectCarrierMapDTO;
		event: MouseEvent;
		cardIndex: number;
		wasExpanded: any;
		shiftKey: any;
	}> = new EventEmitter();
	@Output() editAllocationEvent: EventEmitter<{
		projectCarrierMap: ProjectCarrierMapDTO;
	}> = new EventEmitter();

	@ViewChildren(CdkVirtualScrollViewport) viewports: QueryList<CdkVirtualScrollViewport>;

	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;
	IS_NULL_OR_UNDEFINED = IS_NULL_OR_UNDEFINED;
	DATE_MDY = DATE_MDY;
	IS_NOT_EMPTY = IS_NOT_EMPTY;
	COPY_TO_CLIPBOARD_MSGS = COPY_TO_CLIPBOARD_MSGS;
	SHOW_IN_NETWORK_LOGO = SHOW_IN_NETWORK_LOGO;
	GET_CARRIER_BUSINESS_NAME = GET_CARRIER_BUSINESS_NAME;

	step = null;
	selectedMatch: ProjectCarrierMapDTO = null;
	contactList: ContactsTable[] = null;
	isContactsListInitialised: boolean = false;
	accountId: number;
	selectedProjectCarrierMap: Observable<ProjectCarrierMapDTO>;
	ACCOUNT_TYPE = ACCOUNT_TYPE;
	USER_ACCOUNT_TYPE: string = null;
	environment = environment;

	constructor(
		private urlService: UrlService,
		private restService: RestService,
		public utilService: UtilService,
		public cargochief: Cargochief,
		public projectHelperService: ProjectHelperService,
		private segmentWrapper: SegmentWrapperService,
		private carrierFeedbackService: CarrierFeedbackService,
		public accountSettings: AccountSettingsService,
		private router: Router,
		public productsService: ProductsService,
		private userCarrierMapHelperService: UserCarrierMapHelperService,
		private sharedService: SharedService,
		private cd: ChangeDetectorRef,
		public carrierVettingService: CarrierVettingService,
		public haulpayService: HaulpayService,
		public integrationService: IntegrationService,
		private store: Store
	) {
		super();
		this.USER_ACCOUNT_TYPE = this.cargochief.USER_ACCOUNT_TYPE;
		this.isCarrierVettingIntegrationEnabled =
			this.integrationService.isCarrierVettingIntegrationEnabled();
	}

	ngOnInit(): void {
		this.accountId = this.cargochief.ACCOUNT_ID;
	}

	// CN-8774: single gate for the Onboard Carrier link, shared by the kanban and standard
	// card layouts. Evaluated per change-detection so it reflects the current login-setup /
	// vetting-integration state rather than a one-time ngOnInit snapshot.
	showOnboardCarrier(carrier: CarrierDTOResponse): boolean {
		return (
			this.enableOnboardCarrier &&
			this.integrationService.isIntegrationUrlAvailable() &&
			SHOW_ONBOARD_CARRIER_LINK(
				this.CARRIER_QUALITY_ENABLED,
				this.carrierVettingService,
				this.getVettingSource(carrier.accountCarrierMaps),
				carrier.customerCarrier,
				carrier.dot,
				this.isCarrierVettingIntegrationEnabled,
				this.getVettingSourceId(carrier.accountCarrierMaps),
				this.carrierVettingService.isCarrierOnboarded(carrier.id)
			)
		);
	}

	// CN-8774: mirrors pannel-header.onboardCarrier() so the Engaged tab uses the same
	// single-carrier onboarding flow (vetting invitation email) as the Recommended tab.
	onboardCarrier(carrier: CarrierDTOResponse): void {
		const integrationType = this.integrationService.getEnabledCarrierVettingIntegration();
		const payload: any = this.utilService.getUserDataForSegmentEvent();
		payload.carrier = carrier;
		payload.page = 'engaged_tab';

		this.integrationService.showMyCarrierEmailConfirmation(
			carrier.businessName,
			carrier.dot,
			carrier.id,
			payload,
			null,
			null,
			integrationType
		);
	}

	setStep(index: number) {
		this.isContactsListInitialised = false;
		this.step = index;
		this.selectedMatch = this.projectCarrierMapList[index];
		this.populateContactsList();
		this.setSelectedProjectCarrierMap(this.selectedMatch);
		this.isContactsListInitialised = true;

		this.calculateViewportSize();
	}

	matchClosed() {
		this.selectedMatch = null;
		this.setSelectedProjectCarrierMap(this.selectedMatch);
	}

	emitPanelClickedEvent(
		projectCarrierMap: ProjectCarrierMapDTO,
		event: MouseEvent,
		cardIndex: number,
		isCheckCircleClicked: boolean
	) {
		// Prevent Material header's default toggle from racing our state update
		event.preventDefault();
		event.stopPropagation();

		const wasExpanded = this.step === cardIndex; // <-- capture BEFORE toggle

		// CN-8312 V2: shift-click multi-select is V1 behavior only. In V2 the bulk-select
		// gutter checkbox handles selection; clicks on the card just expand/collapse.
		const shift = event.shiftKey && !this.hideLastColumn;

		if (shift) {
			// V1 only: shift-mode = multi-select only, never expand
			this.closeAllPannels();
		} else {
			// Single click: expand or collapse the card. Selection-array updates happen
			// in the parent based on layout mode (V1 sets selection on click; V2 doesn't).
			if (wasExpanded) {
				this.closeAllPannels();
			} else {
				this.setStep(cardIndex);
			}
		}

		this.panelClicked.emit({ projectCarrierMap, event, cardIndex, wasExpanded, shiftKey: shift });
		this.calculateViewportSize(); // see section C
	}

	/** CN-8312 V2: emit when the bulk-select gutter checkbox is toggled for a card. */
	emitBulkSelectToggle(projectCarrierMap: ProjectCarrierMapDTO) {
		this.bulkSelectToggle.emit(projectCarrierMap);
	}

	closeAllPannels() {
		this.step = null;
		this.selectedMatch = null;
		this.setSelectedProjectCarrierMap(null);

		this.calculateViewportSize();
	}

	populateContactsList() {
		this.contactList = CONTACT_LIST_TABLE(this.selectedMatch.carrier.contacts);
		if (IS_EMPTY_LIST(this.contactList)) {
			this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.carrier_missing_contact_info, {
				carrier: this.getCarrierInfo(),
			});
		}
	}

	getCarrierInfo() {
		return {
			businessName: this.selectedMatch.carrier.businessName,
			mc: this.selectedMatch.carrier.mc,
			dot: this.selectedMatch.carrier.dot,
			carrierId: this.selectedMatch.carrier.id,
		};
	}

	updateContactsList(updatedList: Array<ContactsTable>) {
		this.contactList = [...updatedList];
	}

	emitOpenFullProfileEventEvent(projectCarrierMap: ProjectCarrierMapDTO, event: MouseEvent) {
		this.router.navigate([], {
			queryParams: {
				fullProfile: 'true',
			},
			queryParamsHandling: 'merge',
		});
		this.openFullProfileEvent.emit({ projectCarrierMap, event });
	}

	async reportDataAction(dataType: string, carrier: CarrierDTOResponse) {
		const capacitySearch: Partial<TruckSearchTable> = {
			businessName: carrier.businessName,
			carrierId: carrier.id,
			firmMc: carrier.mc,
			firmDot: carrier.dot,
			carrierHQ: carrier?.city + ',' + carrier?.state,
			originCity: this.projectDTO?.originCity,
			originState: this.projectDTO?.originState,
			destCity: this.projectDTO?.destCity,
			destState: this.projectDTO?.destState,
			lanePath: BUILD_LANE(
				FORMAT_LOCATION(this.projectDTO?.originCity, this.projectDTO?.originState, null),
				FORMAT_LOCATION(this.projectDTO?.destCity, this.projectDTO?.destState, null),
				this.projectDTO?.equipmentType
			),
			phone: carrier.phone,
			drivers: carrier.drivers,
			trailerTypes: this.projectDTO?.equipmentType,
		};
		await this.carrierFeedbackService.reportDataAction(
			dataType,
			this.searchTruckForm,
			capacitySearch
		);
	}

	getUserIdAssigned(carrierId: string): number {
		return this.userCarrierMapList.filter((cMap: any) => cMap.carrierId == carrierId)[0].assigneeId;
	}

	setAssignee(user: Owner, carrierId: string) {
		if (!this.utilService.isNullOrUndefined(user)) {
			const cMap: any = this.userCarrierMapList.filter(
				(cMap: any) => cMap.carrierId == carrierId
			)[0];
			if (
				this.utilService.isNullOrUndefined(cMap) ||
				(!this.utilService.isNullOrUndefined(cMap) && cMap.assigneeId != user.userId)
			) {
				this.addUserCarrierMap(user, carrierId);
			}
		} else if (
			this.utilService.isNullOrUndefined(user) &&
			!this.utilService.isNullOrUndefined(this.userCarrierMap)
		) {
			const userCarrierMapId: string = this.userCarrierMapList.filter(
				(cMap: any) => cMap.carrierId == carrierId
			)[0].id;
			if (!this.utilService.isNullOrUndefined(userCarrierMapId)) {
				this.deleteUserCarrierMap(carrierId, userCarrierMapId);
			}
		}
	}

	addUserCarrierMap(user: Owner, carrierId: string) {
		const payload = this.userCarrierMapHelperService.createPayload(user.userId, carrierId, false);
		const url = this.urlService.userCarrierMap();
		this.restService
			.POST(url, payload)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe(
				(result) => {
					this.userCarrierMap[carrierId] = user.name;
					this.utilService.showSnackbar('User Assigned.', SNACKBAR.SUCCESS, SNACKBAR.ACTION);
					this.sharedService.sendCarrierAssignedEvent({
						deleted: false,
						carrierId: carrierId,
						assigneeName: user.name,
						userCarrierMap: result.data,
					});
					this.userCarrierMapList = this.userCarrierMapList.filter(
						(cMap: any) => cMap.carrierId != carrierId
					);
					this.userCarrierMapList.push(result.data);
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to set Assignee. Please try again.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);
	}

	deleteUserCarrierMap(carrierId: string, userCarrierMapId: string) {
		const payload = {
			userCarrierMapIDs: [userCarrierMapId],
		};
		const url = this.urlService.userCarrierMap();
		this.restService
			.DELETE(url, payload)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe(
				(result) => {
					delete this.userCarrierMap[carrierId];
					this.utilService.showSnackbar('Assignee removed.', SNACKBAR.SUCCESS, SNACKBAR.ACTION);
					this.sharedService.sendCarrierAssignedEvent({
						deleted: true,
						carrierId: carrierId,
					});
					this.userCarrierMapList = this.userCarrierMapList.filter(
						(cMap: any) => cMap.carrierId != carrierId
					);
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to delete Assignee. Please try again.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);
	}

	drag(event: any, projectCarrierMap: ProjectCarrierMapDTO) {
		this.cd.detach();
		let projectCarrierMapIds: string;
		if (this.selectedProjectCarrierMapIDs.length === 0) {
			projectCarrierMapIds = projectCarrierMap.id;
		} else {
			projectCarrierMapIds = this.selectedProjectCarrierMapIDs.join();
		}
		event.dataTransfer.setData('carrierId', projectCarrierMapIds);
	}

	dragend() {
		this.cd.reattach();
		this.selectedProjectCarrierMapIDs = [];
	}

	isFrequencyValidNumber(frequency): boolean {
		return !/\D/.test(frequency);
	}

	getCcScoreDisplay(accountCarrierMaps: AccountCarrierMapDTO[], carrier: any) {
		if (!accountCarrierMaps || accountCarrierMaps.length === 0) {
			return null;
		}
		let score = null;
		const accountCarrierMap = accountCarrierMaps.find((acm) => acm.accountId === this.accountId);
		score = accountCarrierMap?.ccScore;
		if (
			IS_EMPTY(accountCarrierMap?.vettingOverallDisplay) &&
			IS_NOT_EMPTY(carrier.vettingOverallDisplay)
		) {
			score = carrier.ccScore;
		}
		return this.sharedService.getCarrierScoreToDisplay(score, carrier.active);
	}

	getAccountCarrierMap(accountCarrierMaps: AccountCarrierMapDTO[], carrier: any) {
		if (!accountCarrierMaps || accountCarrierMaps.length === 0) {
			return null;
		}
		return accountCarrierMaps.find((acm) => acm.accountId === this.accountId);
	}

	getVettingSourceId(accountCarrierMaps: AccountCarrierMapDTO[]): any {
		return accountCarrierMaps?.find((acm) => acm.accountId === this.accountId)?.vettingSourceId;
	}

	getVettingSource(accountCarrierMaps: AccountCarrierMapDTO[]): any {
		return accountCarrierMaps?.find((acm) => acm.accountId === this.accountId)?.vettingSource;
	}

	onProjectCarrierMapContact(projectCarrierMap): void {
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.CONTACT_UPDATE({ projectCarrierMap: projectCarrierMap })
		);
		this.setSelectedProjectCarrierMap(projectCarrierMap);
	}

	async setSelectedProjectCarrierMap(projectCarrierMap: ProjectCarrierMapDTO) {
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(takeUntil(this.isDestroyed$), take(1))
			.subscribe(async (state) => {
				let selectPcm = null;
				if (projectCarrierMap) {
					selectPcm = state.allProjectCarrierMapsList.find(
						(pcm) => pcm.id === projectCarrierMap.id
					);
				}
				this.selectedProjectCarrierMap = of(selectPcm);
			});
	}

	calculateViewportSize() {
		setTimeout(() => {
			this.viewports.forEach((viewport) => viewport.checkViewportSize());
		}, 0);
	}

	trackByFn(index: number, item: any): number {
		return item.id;
	}

	emitEditAllocationEvent(projectCarrierMap: ProjectCarrierMapDTO, event: MouseEvent): void {
		event.stopPropagation();
		this.editAllocationEvent.emit({ projectCarrierMap });
	}

	/**
	 * Get the allocation for a carrier if it exists
	 */
	getCarrierAllocation(carrierId: string): any {
		return this.carrierAllocationsMap?.[carrierId] || null;
	}

	/**
	 * Check if a carrier has an active allocation
	 */
	hasActiveAllocation(carrierId: string): boolean {
		const allocation = this.getCarrierAllocation(carrierId);
		return allocation?.enabled === true;
	}

	/**
	 * Get a human-readable allocation summary
	 */
	getAllocationSummary(carrierId: string): string {
		const allocation = this.getCarrierAllocation(carrierId);

		if (!allocation) {
			return '';
		}

		const type = allocation.allocationType === 'PERCENTAGE' ? '%' : ' loads';
		const period = allocation.allocationPeriod?.toLowerCase() || 'period';
		return `${allocation.allocationValue}${type} ${period}`;
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}
